import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EmptyState } from '@/components/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { StoreComparisonResponse } from './types';

/**
 * 「会社横断比較」タブ (company_admin のみ、設計書§1-1/§3-4)。ロースターと軸が異なり
 * (横軸が店舗になる)、1人材像を選んで店舗間の充足人数を比較する。
 */

type RoleProfileMaster = { role_profile_id: string; label: string };

export function StoreComparisonTab() {
  const [roleProfileId, setRoleProfileId] = useState('');
  const { data: roleProfiles = [] } = useQuery<RoleProfileMaster[]>({
    queryKey: ['role-profiles'], queryFn: () => apiFetch('/masters/role-profiles'),
  });

  const { data, isLoading, isError } = useQuery<StoreComparisonResponse>({
    queryKey: ['talent-dashboard-store-comparison', roleProfileId],
    queryFn: () => apiFetch<StoreComparisonResponse>(`/talent/dashboard/store-comparison?role_profile_id=${encodeURIComponent(roleProfileId)}`),
    enabled: !!roleProfileId,
    retry: false,
  });

  const stores = data?.stores ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded border border-alert-border bg-alert-bg px-3 py-2">
        <p className="text-xs text-alert-text">⚠️ ロースターとは軸が異なります。ここでは店舗を横に並べて比較するため、比較する人材像を1つ選んでください。</p>
      </div>

      <div className="max-w-xs">
        <label className="block text-xs text-text-sub mb-1" htmlFor="store-comparison-role">比較する人材像</label>
        <Select value={roleProfileId || '__unselected__'} onValueChange={(v) => setRoleProfileId(v === '__unselected__' ? '' : v)}>
          <SelectTrigger id="store-comparison-role" aria-label="比較する人材像"><SelectValue placeholder="選択してください" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__unselected__">選択してください</SelectItem>
            {roleProfiles.map((rp) => <SelectItem key={rp.role_profile_id} value={rp.role_profile_id}>{rp.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!roleProfileId ? (
        <p className="text-sm text-text-sub">上の「比較する人材像」からまず選択してください。</p>
      ) : isLoading ? (
        <div role="status" className="text-text-sub">読み込み中…</div>
      ) : isError ? (
        <p role="alert" className="text-sm text-alert-text">
          {/* company_admin (all_stores) 以外は403で防御 (nav側で非表示だが直接叩かれた場合の対策、設計書§1-1) */}
          会社横断比較の取得に失敗しました。
        </p>
      ) : stores.length === 0 ? (
        <EmptyState message="比較対象の店舗が見つかりませんでした" />
      ) : (
        <div className="border border-surface-border rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-surface-sunken text-text-sub">
                <tr>
                  <th scope="col" className="text-left px-3 py-2">店舗</th>
                  <th scope="col" className="text-right px-3 py-2">充足人数</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {stores.map((s) => (
                  <tr key={s.node_id}>
                    <td className="px-3 py-2 text-text-body break-words">{s.node_name}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-text-body">
                      {s.satisfied_member_count} / {s.total_member_count}名
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
